import { AbstractControl, ValidatorFn } from '@angular/forms';
import { hasProperty, isNil, isRecord } from '@fmnts/core';
import { Vector2dLike } from '@fmnts/core/math';

export const DEFAULT_SIGNATURE_MIN_LINE_COUNT = 1;
export const DEFAULT_SIGNATURE_MIN_POINT_COUNT = 5;

export interface SignatureDataValidatorArgs {
  minLineCount?: number;
  minPointCount?: number;
}

export interface SignatureDataValidationError {
  minLineCount: number;
  minPointCount: number;
}

export const hasSignatureDataError = hasProperty(
  'signatureData',
  (u): u is SignatureDataValidationError => isRecord(u),
);

/**
 * Validates raw signature stroke data (`Point[][]`).
 *
 * A signature is valid when it has at least `minLineCount` strokes and at least
 * one stroke with more than `minPointCount` points.
 */
export function validateSignatureData(
  signatureData: unknown,
  minLineCount = DEFAULT_SIGNATURE_MIN_LINE_COUNT,
  minPointCount = DEFAULT_SIGNATURE_MIN_POINT_COUNT,
): boolean {
  if (isNil(signatureData)) {
    return false;
  }

  if (!Array.isArray(signatureData) || signatureData.length <= 0) {
    return false;
  }

  if (hasLinesWithInvalidPoints(signatureData)) {
    return false;
  }

  if (
    !hasMinLinesWithEnoughPoints(signatureData, minLineCount, minPointCount)
  ) {
    return false;
  }

  return true;
}

/**
 * Validator that checks signature stroke structure.
 * Empty values are skipped and validated via `required`.
 */
export function signatureDataValidator(
  args: SignatureDataValidatorArgs = {},
): ValidatorFn {
  const minLineCount = args.minLineCount ?? DEFAULT_SIGNATURE_MIN_LINE_COUNT;
  const minPointCount = args.minPointCount ?? DEFAULT_SIGNATURE_MIN_POINT_COUNT;

  return (
    control: AbstractControl,
  ): Record<'signatureData', SignatureDataValidationError> | null => {
    const signatureLines = extractSignatureLines(control.value);

    if (signatureLines === null) {
      return null;
    }

    if (validateSignatureData(signatureLines, minLineCount, minPointCount)) {
      return null;
    }

    return { signatureData: { minLineCount, minPointCount } };
  };
}

function extractSignatureLines(value: unknown): unknown[] | null {
  if (isNil(value)) {
    return null;
  }

  if (Array.isArray(value)) {
    return value;
  }

  if (isRecord(value) && Array.isArray(value['data'])) {
    return value['data'];
  }

  return null;
}

function hasLinesWithInvalidPoints(lines: unknown[]): boolean {
  return lines.some((line) => {
    if (!Array.isArray(line)) {
      return true;
    }

    return line.some((point) => !isSignaturePoint(point));
  });
}

function isSignaturePoint(point: unknown): point is Vector2dLike {
  return (
    isRecord(point) &&
    typeof point['x'] === 'number' &&
    typeof point['y'] === 'number' &&
    !Number.isNaN(point['x']) &&
    !Number.isNaN(point['y'])
  );
}

function hasMinLinesWithEnoughPoints(
  lines: unknown[],
  minLineCount: number,
  minPointCount: number,
): boolean {
  if (lines.length < minLineCount) {
    return false;
  }

  const linesWithEnoughPoints = lines.filter(
    (line) => Array.isArray(line) && line.length > minPointCount,
  );

  return linesWithEnoughPoints.length > 0;
}
